/**
 * El blog del sitio: la lista de entradas y, abierta una, su pantalla de
 * escritura (`BlogPostEditor`).
 *
 * Es el CMS por dentro, pero aquí no se dice: quien arranca un sitio sabe qué
 * es un blog y no tiene por qué saber qué es una colección. Las entradas viven
 * en la base —como las ofertas— y se publican al guardarlas, sin republicar el
 * sitio. El aspecto de la página del artículo no se toca aquí: eso es del
 * editor, como el resto del diseño.
 *
 * La entrada abierta va en `record` —su id, o `new`—, como las fichas de
 * Contactos: así queda en la dirección y el botón atrás vuelve a la lista.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  IconAdjustmentsHorizontal,
  IconArticle,
  IconClock,
  IconCalendarEvent,
  IconCheck,
  IconDots,
  IconEye,
  IconExternalLink,
  IconFileText,
  IconLink,
  IconPencil,
  IconPlus,
  IconLayoutGrid,
  IconSearch,
  IconSend,
  IconTag,
  IconTrash,
  IconX,
  IconUsers,
} from '@tabler/icons-react'
import { BlogAuthorsDialog } from './BlogAuthorsDialog'
import { OfferStatusSelect } from './OfferStatusSelect'
import { VelticaMascot } from '@webbuilder/ui'
import { blogAuthorNames, type BlogPost, type BlogPostState } from '@webbuilder/schemas'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { SectionRefresh } from '../../SectionRefresh'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { useI18n } from '../../../lib/i18n'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { deleteBlogPost, getBlog, serverErrorCode, updateBlogPost } from '../../../lib/api'
import { PlanLimitModal } from '../PlanLimitModal'
import { HeaderRefreshButton } from '../ListSelection'
import { PlanUsageSummary } from '../PlanUsageSummary'
import type { BillingPlan } from '../../../lib/api'
import { ConfirmDialog } from '../ConfirmDialog'
import { useToast } from '../../Toast'
import { openOnRowClick } from '../rowOpen'
import { errorMessage, failureDetail } from './offerShared'
import { BlogPostEditor, blogPostUrl } from './BlogPostEditor'

/* Los mismos tonos que el estado de una oferta: publicada en verde, borrador en
   gris, y la programada con el de «en revisión» —está decidida, pero aún no se
   ve—. */
type Filter = 'all' | BlogPostState
/* Cada estado con su icono, como el filtro de Ofertas. */
const FILTERS: { id: Filter; icon: typeof IconLayoutGrid }[] = [
  { id: 'all', icon: IconLayoutGrid },
  { id: 'published', icon: IconEye },
  { id: 'scheduled', icon: IconClock },
  { id: 'draft', icon: IconPencil },
]

export function BlogSection({ projectId, token, siteOrigin, plan, onOpenPlans, record, onRecordChange }: {
  projectId: string
  token: string
  /** La entrada abierta: su id, `new` para una nueva, o nada para la lista. */
  /** El dominio del sitio publicado; sin él las entradas no tienen enlace todavía. */
  siteOrigin: string | null
  /** Para el cupo de entradas publicadas en la cabecera, como en Ofertas. */
  plan: BillingPlan
  onOpenPlans: () => void
  record: string | null
  onRecordChange?: ((record: string | null) => void) | undefined
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const searchPlaceholder = useSearchPlaceholder('blog.search')
  const blog = useSectionResource(token, [projectId, 'blog'], () => getBlog(projectId, token))
  const loader = useRecordLoader(blog.loading)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  /* El tope de entradas publicadas: al chocar con él, desde la lista o desde
     la entrada abierta, sale la misma ventana que en Ofertas y Páginas. */
  const [limitOpen, setLimitOpen] = useState(false)
  const [authorsOpen, setAuthorsOpen] = useState(false)
  const limitModal = limitOpen ? (
    <PlanLimitModal
      resource={t('usage.stat.blogPosts').toLowerCase()}
      kind="blogPosts"
      currentPlan={plan}
      onClose={() => setLimitOpen(false)}
      onOpenPlans={() => { setLimitOpen(false); onOpenPlans() }}
    />
  ) : null
  /* La categoría, en el mismo popover que el estado: '' es «todas». */
  const [category, setCategory] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!filterOpen) return
    const close = (event: MouseEvent) => { if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false) }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [filterOpen])
  const [confirmDelete, setConfirmDelete] = useState<BlogPost | null>(null)
  const [menu, setMenu] = useState<{ id: string; top: number; left: number; up: boolean } | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const basePath = blog.data?.settings?.basePath ?? 'blog'
  const copyLink = async (post: BlogPost) => {
    if (!siteOrigin) return
    try {
      await navigator.clipboard.writeText(blogPostUrl(siteOrigin, basePath, post.slug))
      setCopiedId(post.id)
      window.setTimeout(() => setCopiedId((current) => (current === post.id ? null : current)), 1600)
    } catch {
      showToast(t('blog.link.copyFailed'), 'error')
    }
  }
  const menuRef = useRef<HTMLDivElement | null>(null)

  const [localOpen, setLocalOpen] = useState<string | null>(null)
  const open = onRecordChange ? record : localOpen
  const setOpen = (next: string | null) => (onRecordChange ? onRecordChange(next) : setLocalOpen(next))

  useEffect(() => {
    if (!menu) return
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (menuRef.current?.contains(target) || target.closest('[data-blog-menu-trigger]')) return
      setMenu(null)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenu(null) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const posts = useMemo(() => blog.data?.posts ?? [], [blog.data])
  /* Las categorías del blog, de las más usadas a las menos: son las que se
     sugieren al escribir una entrada. */
  const knownCategories = useMemo(() => {
    const tally = new Map<string, number>()
    for (const post of posts) for (const category of post.categories) tally.set(category, (tally.get(category) ?? 0) + 1)
    return [...tally].map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  }, [posts])
  const knownAuthors = useMemo(() => {
    const tally = new Map<string, number>()
    for (const post of posts) for (const name of blogAuthorNames(post.authorName)) tally.set(name, (tally.get(name) ?? 0) + 1)
    return [...tally].map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  }, [posts])
  /* Los autores que ofrece el selector de la entrada: los que firman y también los
     creados en Blog › Autores que aún no firman ninguna (con 0). */
  const pickerAuthors = useMemo(() => {
    const out = [...knownAuthors]
    for (const profile of blog.data?.authors ?? []) {
      if (!out.some((author) => author.name.toLocaleLowerCase() === profile.name.toLocaleLowerCase())) out.push({ name: profile.name, count: 0 })
    }
    return out
  }, [knownAuthors, blog.data?.authors])
  /* Las caras del botón «Autores»: los que firman entradas y también los creados en
     Blog › Autores que aún no firman ninguna; primero los que tienen foto. */
  const headerAuthors = useMemo(() => {
    const profiles = blog.data?.authors ?? []
    const names = new Map<string, { name: string; photo: string }>()
    for (const author of knownAuthors) names.set(author.name.toLocaleLowerCase(), { name: author.name, photo: '' })
    for (const profile of profiles) {
      const key = profile.name.toLocaleLowerCase()
      names.set(key, { name: names.get(key)?.name ?? profile.name, photo: profile.avatarUrl })
    }
    return [...names.values()].sort((a, b) => Number(Boolean(b.photo)) - Number(Boolean(a.photo)))
  }, [knownAuthors, blog.data?.authors])
  const counts = useMemo(() => {
    const result: Record<Filter, number> = { all: posts.length, published: 0, scheduled: 0, draft: 0 }
    for (const post of posts) result[post.state] += 1
    return result
  }, [posts])
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    return posts.filter((post) => (filter === 'all' || post.state === filter)
      && (!category || post.categories.includes(category))
      && (!needle
      || matchesSearch(post.title, needle)
      || matchesSearch(post.slug, needle)
      || post.categories.some((category) => matchesSearch(category, needle))))
  }, [posts, filter, category, query])

  if (open) {
    return (
      <>
      <BlogPostEditor
        key={open}
        projectId={projectId}
        token={token}
        postId={open === 'new' ? null : open}
        basePath={basePath}
        siteOrigin={siteOrigin}
        knownCategories={knownCategories}
        knownAuthors={pickerAuthors}
        onCategoriesChanged={() => { void blog.refresh() }}
        onBack={() => { void blog.refresh(); setOpen(null) }}
        onCreated={(post) => setOpen(post.id)}
        onDeleted={() => { void blog.refresh(); setOpen(null) }}
        onLimitReached={() => setLimitOpen(true)}
      />
      {limitModal}
      </>
    )
  }

  if (loader.visible) return <SectionLoader loader={loader} label={t('blog.loading')} />

  const usage = blog.data?.usage
  const dateOf = (post: BlogPost) => (post.state === 'draft' ? post.updatedAt : post.publishedAt ?? post.updatedAt)
  const dateLabel = (post: BlogPost) => t(
    post.state === 'scheduled' ? 'blog.date.scheduled' : post.state === 'published' ? 'blog.date.published' : 'blog.date.edited',
    { date: formatDashboardListDate(dateOf(post)) },
  )

  const setStatus = async (post: BlogPost, status: 'draft' | 'published') => {
    setBusyId(post.id)
    try {
      const saved = await updateBlogPost(projectId, post.id, { status }, token)
      showToast(t(status === 'draft' ? 'blog.toast.draft' : saved.state === 'scheduled' ? 'blog.toast.scheduled' : 'blog.toast.published', {
        date: saved.publishedAt ? formatDashboardListDate(saved.publishedAt) : '',
      }))
      await blog.refresh()
    } catch (error) {
      if (serverErrorCode(error) === 'blog.publishLimitReached') setLimitOpen(true)
      else showToast(errorMessage(error), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const openMenu = (post: BlogPost, button: HTMLElement) => {
    if (menu?.id === post.id) { setMenu(null); return }
    const rect = button.getBoundingClientRect()
    const width = 184
    const height = 190
    const up = window.innerHeight - rect.bottom < height + 20 && rect.top > height + 20
    setMenu({
      id: post.id,
      top: up ? rect.top - height - 6 : rect.bottom + 6,
      left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)),
      up,
    })
  }
  const menuPost = menu ? posts.find((post) => post.id === menu.id) : undefined

  return (
    <div className="dash-projects-view dash-pages-view dash-blog-view dash-loaded-content-in">
      <SectionRefresh refreshing={blog.refreshing} failed={Boolean(blog.error)} />
      {/* Como Ofertas: arriba actualizar y el cupo del plan; crear y filtrar,
          en la barra de la lista. */}
      <DashboardPageHeader
        title={t('blog.title')}
        subtitle={t('blog.subtitle')}
        actionLayout="split"
        actions={(
          <>
            <div className="dash-bookings-header-actions">
              <div className="dash-bookings-header-tools">
                <HeaderRefreshButton refreshing={blog.refreshing} onRefresh={() => { void blog.refresh() }} />
                {/* Los autores, como «Equipo» en Reservas: sus caras y el nombre. */}
                <button type="button" className="dash-secondary-btn dash-catalog-page-btn dash-team-button" aria-label={`${t('blog.authors.title')} (${headerAuthors.length})`} onClick={() => setAuthorsOpen(true)}>
                  {headerAuthors.length ? (
                    <span className="dash-team-button-faces" aria-hidden="true">
                      {headerAuthors.slice(0, 3).map((author) => (
                        <span key={author.name} className="dash-team-button-face">
                          {author.photo ? <img src={author.photo} alt="" /> : author.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase() || <IconUsers size={12} />}
                        </span>
                      ))}
                    </span>
                  ) : <IconUsers size={15} aria-hidden="true" />}
                  {headerAuthors.length > 3 ? <span className="dash-team-button-more" aria-hidden="true">+{headerAuthors.length - 3}</span> : null}
                  <span>{t('blog.authors.button')}</span>
                </button>
              </div>
              {usage ? (
                <div className="dash-bookings-header-usage">
                  <PlanUsageSummary
                    items={[{ label: t('usage.stat.blogPosts'), used: usage.published, limit: usage.limit }]}
                    plan={plan}
                    onOpenPlans={onOpenPlans}
                  />
                </div>
              ) : null}
            </div>
          </>
        )}
      />

      {posts.length > 0 ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-lead">
            <div className="dash-toolbar-search">
              <span className="dash-toolbar-search-icon" aria-hidden="true"><IconSearch size={16} /></span>
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Escape') setQuery('') }}
              />
              {query ? (
                <button type="button" className="dash-toolbar-search-clear" aria-label={t('blog.clearSearch')} onClick={() => setQuery('')}>
                  <IconX size={13} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="dash-toolbar-controls">
            {/* Estado y categoría en un solo popover, el de Ofertas: cuatro
                pastillas con su número ocupaban media barra y competían con la
                búsqueda. El punto dice que hay un filtro puesto. */}
            <div className="dash-filter-wrap" ref={filterRef}>
              <button
                className="dash-filter-trigger"
                type="button"
                aria-label={t('blog.filter.button')}
                data-tooltip={t('blog.filter.button')}
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen((current) => !current)}
              >
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {filter !== 'all' || category ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>
              {filterOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('blog.filter.aria')}>
                  <div className="dash-filter-popover-head">
                    <strong>{t('blog.filter.title')}</strong>
                    <button type="button" aria-label={t('blog.filter.close')} onClick={() => setFilterOpen(false)}><IconX size={15} /></button>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('blog.filter.status')}</span>
                    <div className="dash-filter-options">
                      {FILTERS.map(({ id, icon: Icon }) => (
                        <button key={id} className={filter === id ? 'active' : ''} type="button" onClick={() => setFilter(id)}>
                          <Icon size={14} />
                          <span>{t(id === 'all' ? 'blog.filter.all' : `blog.state.${id}`)}</span>
                          <small className="dash-blog-filter-count">{counts[id]}</small>
                          {filter === id ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                  {knownCategories.length ? (
                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('blog.filter.category')}</span>
                      <div className="dash-filter-options">
                        <button className={!category ? 'active' : ''} type="button" onClick={() => setCategory('')}>
                          <IconLayoutGrid size={14} />
                          <span>{t('blog.filter.all')}</span>
                          {!category ? <IconCheck size={14} /> : null}
                        </button>
                        {knownCategories.map((item) => (
                          <button key={item.name} className={category === item.name ? 'active' : ''} type="button" onClick={() => setCategory(item.name)}>
                            <IconTag size={14} />
                            <span>{item.name}</span>
                            <small className="dash-blog-filter-count">{item.count}</small>
                            {category === item.name ? <IconCheck size={14} /> : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {filter !== 'all' || category ? (
                    <button className="dash-filter-reset" type="button" onClick={() => { setFilter('all'); setCategory('') }}>
                      {t('blog.filter.reset')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('blog.new')} onClick={() => setOpen('new')}>
              <IconPlus size={15} />
              <span className="dash-btn-label dash-btn-label--desktop">{t('blog.new')}</span>
              <span className="dash-btn-label dash-btn-label--compact">{t('blog.newShort')}</span>
            </button>
          </div>
        </header>
      ) : null}

      {blog.error && blog.data === undefined ? (
        <div className="dash-inline-msg">
          <span>
            {t('blog.loadFailed')}
            <code className="dash-failure-detail">{failureDetail(blog.error)}</code>
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void blog.refresh() }}>{t('blog.retry')}</button>
          </div>
        </div>
      ) : posts.length === 0 ? (
        <div className="dash-placeholder">
          <IconArticle size={34} aria-hidden="true" />
          <strong>{t('blog.empty.title')}</strong>
          <span>{t('blog.empty.body')}</span>
          <button type="button" className="dash-primary-btn" onClick={() => setOpen('new')}>
            <IconPlus size={15} /> {t('blog.new')}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('blog.noResults')}</strong>
          <button type="button" className="dash-secondary-btn" onClick={() => { setQuery(''); setFilter('all'); setCategory('') }}>{t('blog.clearSearch')}</button>
        </div>
      ) : (
        <div className="dash-cms-table-scroll">
          <div className="dash-data-table dash-blog-data-table">
            <div className="dash-data-header" role="row">
              <span>{t('blog.col.title')}</span>
              <span>{t('blog.col.status')}</span>
              <span>{t('blog.col.date')}</span>
              <span className="dash-data-header-actions">{t('blog.col.actions')}</span>
            </div>
            <div className="dash-project-list">
              {visible.map((post) => (
                <article className="dash-project-row dash-data-row dash-loaded-content-in" key={post.id} onClick={openOnRowClick(() => setOpen(post.id))}>
                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') setOpen(post.id) }}
                  >
                    {/* Como en Ofertas: el marco de cristal sólo sin portada. Esa
                        variante deja asomar el icono (`overflow: visible`), y con
                        una imagen dentro la portada se salía de la fila. */}
                    {post.coverImage ? (
                      <span className="dash-project-row-thumb dash-page-project-thumb">
                        <img src={post.coverImage} alt="" loading="lazy" />
                      </span>
                    ) : (
                      <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
                          <IconFileText size={13} />
                        </span>
                      </span>
                    )}
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        <strong>{post.title || t('blog.untitled')}</strong>
                      </span>
                      {/* Sin la dirección (/blog/…): el enlace ya está en el botón de
                          la fila. Debajo del título, sólo sus categorías. */}
                      {post.categories.length ? (
                        <span className="dash-project-row-details">{post.categories.join(', ')}</span>
                      ) : null}
                    </span>
                  </div>
                  <div className="dash-list-row-meta">
                    <span className="dash-data-cell" data-label={t('blog.col.status')}>
                      {post.setupPending
                        /* La empezó la IA: el cuerpo es de relleno y no se publica así. */
                        ? <span className="dash-ai-pending-chip is-compact is-static" title={t('aiPending.postHint')}><VelticaMascot outline size={15} />{t('aiPending.chip')}</span>
                        /* Publicar o volver a borrador desde la lista, como en Ofertas. */
                        : (
                          <OfferStatusSelect
                            status={post.status === 'published' ? 'active' : 'draft'}
                            busy={busyId === post.id}
                            blocked={null}
                            compact
                            label={post.title || t('blog.untitled')}
                            liveLabelKey={post.state === 'scheduled' ? 'blog.state.scheduled' : 'blog.state.published'}
                            onSelect={(next) => { void setStatus(post, next === 'active' ? 'published' : 'draft') }}
                            onConnect={() => {}}
                            onOpenPlans={onOpenPlans}
                          />
                        )}
                    </span>
                    <span className="dash-data-cell dash-list-date-cell" data-label={t('blog.col.date')} title={dateLabel(post)}>
                      <IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      {formatDashboardListDate(dateOf(post))}
                    </span>
                  </div>
                  <div className="dash-project-row-actions">
                    {siteOrigin ? (
                      <button
                        type="button"
                        className="dash-icon-btn dash-blog-row-link"
                        aria-label={t(copiedId === post.id ? 'blog.link.copied' : 'blog.link.copy')}
                        data-tooltip={t(copiedId === post.id ? 'blog.link.copied' : 'blog.link.copy')}
                        onClick={(event) => { event.stopPropagation(); void copyLink(post) }}
                      >
                        {copiedId === post.id ? <IconCheck size={15} /> : <IconLink size={15} />}
                      </button>
                    ) : null}
                    <button
                      className="dash-secondary-btn dash-project-row-action dash-open-content-action"
                      type="button"
                      onClick={(event) => { event.stopPropagation(); setOpen(post.id) }}
                    >
                      <IconEye size={14} />
                      <span className="dash-project-row-action-label">{t('common.open')}</span>
                    </button>
                    <div className="dash-project-row-menu-wrap">
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('blog.moreOptions')}
                        data-tooltip={t('blog.options')}
                        aria-expanded={menu?.id === post.id}
                        disabled={busyId === post.id}
                        data-blog-menu-trigger
                        onClick={(event) => { event.stopPropagation(); openMenu(post, event.currentTarget) }}
                      >
                        <IconDots size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {menu && menuPost ? createPortal(
        <div ref={menuRef} className={`project-menu dash-project-row-menu dash-blog-row-menu-portal${menu.up ? ' is-up' : ''}`} role="menu" style={{ top: menu.top, left: menu.left }}>
          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); setOpen(menuPost.id) }}>
            <IconPencil size={14} /> {t('blog.menu.edit')}
          </button>
          {siteOrigin && menuPost.status === 'published' ? (
            <a
              className="project-menu-item"
              href={blogPostUrl(siteOrigin, basePath, menuPost.slug)}
              target="_blank"
              rel="noreferrer"
              role="menuitem"
              onClick={() => setMenu(null)}
            >
              <IconExternalLink size={14} /> {t('blog.link.open')}
            </a>
          ) : null}
          {siteOrigin ? (
            <button
              className="project-menu-item"
              type="button"
              role="menuitem"
              onClick={() => { setMenu(null); void copyLink(menuPost) }}
            >
              <IconLink size={14} /> {t('blog.link.copy')}
            </button>
          ) : null}
          {menuPost.status === 'published' ? (
            <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); void setStatus(menuPost, 'draft') }}>
              <IconFileText size={14} /> {t('blog.menu.toDraft')}
            </button>
          ) : (
            <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenu(null); void setStatus(menuPost, 'published') }}>
              <IconSend size={14} /> {t('blog.menu.publish')}
            </button>
          )}
          <div className="project-menu-rule" role="separator" />
          <button className="project-menu-item danger" type="button" role="menuitem" onClick={() => { setMenu(null); setConfirmDelete(menuPost) }}>
            <IconTrash size={14} /> {t('blog.menu.delete')}
          </button>
        </div>,
        document.body,
      ) : null}

      {limitModal}
      {authorsOpen ? <BlogAuthorsDialog projectId={projectId} token={token} onClose={() => { setAuthorsOpen(false); void blog.refresh() }} /> : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={t('blog.delete.title', { title: confirmDelete.title || t('blog.untitled') })}
          message={t('blog.delete.body')}
          confirmLabel={t('blog.delete.confirm')}
          tone="danger"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => {
            try {
              await deleteBlogPost(projectId, confirmDelete.id, token)
              showToast(t('blog.toast.deleted'))
              setConfirmDelete(null)
              await blog.refresh()
            } catch (error) {
              showToast(errorMessage(error), 'error')
              throw error
            }
          }}
        />
      ) : null}
    </div>
  )
}
